// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Typed API client (design 4.2, 7). Every response is parsed with the shared Zod schemas.
import { z } from 'zod';
import * as s from '@bda/shared';
import { accessToken } from '../auth/auth';
import { runtimeConfig } from '../config';

/** Where a failed request went: method and the path with ids replaced by `:id` (never ids or user-chosen keys). */
export interface RequestTarget {
  method: string;
  route: string;
}

export class ApiError extends Error {
  /** When the request failed, shown with its reference (lib/notify.tsx). */
  readonly at = new Date();
  /** The HTTP API request ID (`apigw-requestid` header); none for network errors and timeouts. */
  readonly requestId?: string;
  readonly method?: string;
  /** The request path with ids replaced by `:id` (routeOf), for the reference line when the caller gives no label. */
  readonly route?: string;
  constructor(
    readonly status: number,
    readonly code: s.ApiErrorCode | 'NETWORK' | 'TIMEOUT' | 'INVALID_RESPONSE',
    message: string,
    readonly details?: Record<string, unknown>,
    /** X-Ray root trace ID of the failed request (`x-trace-id` header or `error.traceId`), shown for troubleshooting. */
    readonly traceId?: string,
    info: { requestId?: string; target?: RequestTarget } = {},
  ) {
    super(message);
    this.requestId = info.requestId || undefined;
    this.method = info.target?.method;
    this.route = info.target?.route;
  }
}

// Every fixed path segment the client sends; anything else (ids, versions, subs, share keys) becomes `:id`, so a
// segment not listed here is hidden rather than shown.
const STATIC_SEGMENTS = new Set([
  'admin',
  'archive',
  'blueprints',
  'blueprints.csv',
  'create',
  'directory',
  'download',
  'duplicate',
  'export',
  'groups',
  'me',
  'packages',
  'preview',
  'publish',
  'rebuild',
  'result',
  'retry',
  'runs',
  'sample',
  'schema',
  'segments',
  'set-preview',
  'set-proposals',
  'sets',
  'shared',
  'shares',
  'start',
  'suggest',
  'summary',
  'summary.csv',
  'text',
  'timeline',
  'unarchive',
  'upload',
  'users',
  'users.csv',
  'versions',
]);

/** The API path without ids: `/runs/01JC.../start` → `/runs/:id/start`. */
export const routeOf = (path: string): string =>
  path
    .split('/')
    .map((seg) => (seg === '' || STATIC_SEGMENTS.has(seg) ? seg : ':id'))
    .join('/');

type TokenFn = (forceRefresh?: boolean) => Promise<string | null>;
let tokenFn: TokenFn = accessToken;
let onUnauthorized: () => void = () => undefined;

/** Test and app hooks. */
export function configureApi(opts: { token?: TokenFn; onUnauthorized?: () => void }): void {
  if (opts.token) tokenFn = opts.token;
  if (opts.onUnauthorized) onUnauthorized = opts.onUnauthorized;
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | undefined>;
  /** Aborts the request after this many milliseconds; an abort becomes ApiError(0, 'TIMEOUT'). */
  timeoutMs?: number;
}

/**
 * Timeout of every run-creating request (createRun, startRun, retryRun, createPreview, and for blueprint sets
 * createSetPreview, createSetFromProposal, createSampleFromPages; Req 2.8).
 * 30 s matches the API Gateway HTTP API integration limit.
 */
export const RUN_REQUEST_TIMEOUT_MS = 30_000;

const isAbort = (e: unknown): boolean => {
  const name = (e as { name?: unknown } | null)?.name;
  return name === 'TimeoutError' || name === 'AbortError';
};

async function send(path: string, opts: RequestOptions, retried = false): Promise<Response> {
  const token = await tokenFn(retried);
  const qs = new URLSearchParams(
    Object.entries(opts.query ?? {}).filter((e): e is [string, string] => e[1] !== undefined && e[1] !== ''),
  ).toString();
  const url = `${runtimeConfig().apiUrl}/api${path}${qs ? `?${qs}` : ''}`;
  const target = targetOf(path, opts);
  let res: Response;
  try {
    res = await fetch(url, {
      method: opts.method ?? 'GET',
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(opts.body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      ...(opts.timeoutMs !== undefined ? { signal: AbortSignal.timeout(opts.timeoutMs) } : {}),
    });
  } catch (e) {
    if (opts.timeoutMs !== undefined && isAbort(e)) {
      throw new ApiError(
        0,
        'TIMEOUT',
        'The request took too long. Check your connection and try again.',
        undefined,
        undefined,
        { target },
      );
    }
    throw new ApiError(
      0,
      'NETWORK',
      'Could not reach the server. Check your connection and try again.',
      undefined,
      undefined,
      { target },
    );
  }
  if (res.status === 401 && !retried) return send(path, opts, true); // refresh once (design 7)
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError(
      401,
      'UNAUTHORIZED',
      'Your session has ended. Sign in again.',
      undefined,
      undefined,
      responseInfo(res, target),
    );
  }
  return res;
}

/** The API's trace ID header (exposed by the HTTP API's CORS configuration). */
export const TRACE_ID_HEADER = 'x-trace-id';
/** The HTTP API's request ID header, on every response including ones that never reached Lambda (CORS-exposed). */
export const REQUEST_ID_HEADER = 'apigw-requestid';

function targetOf(path: string, opts: RequestOptions): RequestTarget {
  return { method: opts.method ?? 'GET', route: routeOf(path) };
}

/** A response's request ID and its request's method and id-free route. */
function responseInfo(res: Response, target: RequestTarget): { requestId?: string; target: RequestTarget } {
  return { requestId: res.headers.get(REQUEST_ID_HEADER) ?? undefined, target };
}

async function errorFrom(res: Response, target: RequestTarget): Promise<ApiError> {
  const parsed = s.apiErrorSchema.safeParse(await res.json().catch(() => null));
  const traceId =
    (parsed.success ? parsed.data.error.traceId : undefined) ?? res.headers.get(TRACE_ID_HEADER) ?? undefined;
  const info = responseInfo(res, target);
  if (parsed.success)
    return new ApiError(
      res.status,
      parsed.data.error.code,
      parsed.data.error.message,
      parsed.data.error.details,
      traceId,
      info,
    );
  return new ApiError(res.status, 'INTERNAL', `Request failed (${res.status}).`, undefined, traceId, info);
}

export async function request<T extends z.ZodType>(
  schema: T,
  path: string,
  opts: RequestOptions = {},
): Promise<z.infer<T>> {
  const res = await send(path, opts);
  if (!res.ok) throw await errorFrom(res, targetOf(path, opts));
  const data = res.status === 204 ? undefined : await res.json();
  const parsed = schema.safeParse(data);
  if (!parsed.success)
    throw new ApiError(
      res.status,
      'INVALID_RESPONSE',
      'The server returned an unexpected response.',
      undefined,
      res.headers.get(TRACE_ID_HEADER) ?? undefined,
      responseInfo(res, targetOf(path, opts)),
    );
  return parsed.data;
}

export async function requestBlob(
  path: string,
  query?: Record<string, string | undefined>,
): Promise<{ blob: Blob; fileName: string }> {
  const res = await send(path, { query });
  if (!res.ok) throw await errorFrom(res, targetOf(path, {}));
  const cd = res.headers.get('content-disposition') ?? '';
  const fileName = /filename="([^"]+)"/.exec(cd)?.[1] ?? 'download';
  return { blob: await res.blob(), fileName };
}

// ---------- endpoint functions ----------

const empty = z.unknown();

export const api = {
  me: () => request(s.meResponse, '/me'),
  directoryUsers: (q: string) => request(s.directoryUsersResponse, '/directory/users', { query: { q } }),
  directoryGroups: () => request(s.directoryGroupsResponse, '/directory/groups'),

  listBlueprints: (q: { q?: string; docType?: string; archived?: 'true' | 'false'; cursor?: string }) =>
    request(s.blueprintListResponse, '/blueprints', { query: q }),
  createBlueprint: (body: z.input<typeof s.createBlueprintRequest>) =>
    request(s.blueprintSchema, '/blueprints', { method: 'POST', body }),
  getBlueprint: (id: string) => request(s.blueprintDetailResponse, `/blueprints/${encodeURIComponent(id)}`),
  updateBlueprint: (id: string, body: z.input<typeof s.updateBlueprintRequest>) =>
    request(s.blueprintSchema, `/blueprints/${encodeURIComponent(id)}`, { method: 'PUT', body }),
  publish: (id: string, changeNote: string) =>
    request(
      z.object({ version: z.number(), publishedAt: z.string() }),
      `/blueprints/${encodeURIComponent(id)}/publish`,
      {
        method: 'POST',
        body: { changeNote },
      },
    ),
  getVersion: (id: string, v: number) =>
    request(s.blueprintVersionSchema, `/blueprints/${encodeURIComponent(id)}/versions/${v}`),
  archive: (id: string, archived: boolean) =>
    request(s.blueprintSchema, `/blueprints/${encodeURIComponent(id)}/${archived ? 'archive' : 'unarchive'}`, {
      method: 'POST',
    }),
  duplicate: (id: string) =>
    request(s.blueprintSchema, `/blueprints/${encodeURIComponent(id)}/duplicate`, { method: 'POST' }),
  suggest: (runId: string) => request(s.suggestResponse, '/blueprints/suggest', { method: 'POST', body: { runId } }),

  createRun: (body: z.input<typeof s.createRunRequest>) =>
    request(s.createRunResponse, '/runs', { method: 'POST', body, timeoutMs: RUN_REQUEST_TIMEOUT_MS }),
  renewUpload: (id: string) =>
    request(s.createRunResponse, `/runs/${encodeURIComponent(id)}/upload`, { method: 'POST' }),
  startRun: (id: string) =>
    request(s.runSchema, `/runs/${encodeURIComponent(id)}/start`, {
      method: 'POST',
      timeoutMs: RUN_REQUEST_TIMEOUT_MS,
    }),
  /** Empty body repeats the run; `setId` + `setVersion` runs its document against a set ("Run on test document"). */
  retryRun: (id: string, body: z.input<typeof s.retryRunRequest> = {}) =>
    request(s.runSchema, `/runs/${encodeURIComponent(id)}/retry`, {
      method: 'POST',
      body,
      timeoutMs: RUN_REQUEST_TIMEOUT_MS,
    }),
  createPreview: (body: z.input<typeof s.createPreviewRequest>) =>
    request(s.runSchema, '/runs/preview', { method: 'POST', body, timeoutMs: RUN_REQUEST_TIMEOUT_MS }),
  getRun: (id: string) => request(s.runSchema, `/runs/${encodeURIComponent(id)}`),
  getResult: (id: string) => request(s.runResultResponse, `/runs/${encodeURIComponent(id)}/result`),
  /** `setId`: one set's runs, newest first (blueprint-sets, the set page's Runs section). */
  listRuns: (q: {
    blueprintId?: string;
    setId?: string;
    status?: string;
    from?: string;
    to?: string;
    cursor?: string;
  }) => request(s.runListResponse, '/runs', { query: q }),

  // Blueprint sets (blueprint-sets design "Routes"); the API returns 404 for all of them while sets are disabled.
  listSets: (q: { archived?: 'true' | 'false'; cursor?: string } = {}) =>
    request(s.setListResponse, '/sets', { query: q }),
  createSet: (body: z.input<typeof s.createSetRequest>) => request(s.setSchema, '/sets', { method: 'POST', body }),
  getSet: (id: string) => request(s.setDetailResponse, `/sets/${encodeURIComponent(id)}`),
  updateSet: (id: string, body: z.input<typeof s.updateSetRequest>) =>
    request(s.setSchema, `/sets/${encodeURIComponent(id)}`, { method: 'PUT', body }),
  publishSet: (id: string, changeNote: string) =>
    request(s.setVersionResponse, `/sets/${encodeURIComponent(id)}/publish`, {
      method: 'POST',
      body: { changeNote } satisfies z.input<typeof s.publishSetRequest>,
    }),
  getSetVersion: (id: string, v: number) =>
    request(s.setVersionResponse, `/sets/${encodeURIComponent(id)}/versions/${v}`),
  archiveSet: (id: string) => request(s.setSchema, `/sets/${encodeURIComponent(id)}/archive`, { method: 'POST' }),
  duplicateSet: (id: string) => request(s.setSchema, `/sets/${encodeURIComponent(id)}/duplicate`, { method: 'POST' }),
  /** Starts a Set_Preview_Run on the stored Sample_Packet (Req 11); creates BDA resources, hence the run timeout. */
  createSetPreview: (body: z.input<typeof s.createSetPreviewRequest>) =>
    request(s.runSchema, '/runs/set-preview', { method: 'POST', body, timeoutMs: RUN_REQUEST_TIMEOUT_MS }),
  getSetProposal: (runId: string) =>
    request(s.setProposalStatusResponse, `/set-proposals/${encodeURIComponent(runId)}`),
  /** Creates draft blueprints and the Set_Draft from a reviewed proposal (Req 10.6, 10.7). */
  createSetFromProposal: (runId: string, body: z.input<typeof s.createSetFromProposalRequest>) =>
    request(s.createSetFromProposalResponse, `/set-proposals/${encodeURIComponent(runId)}/create`, {
      method: 'POST',
      body,
      timeoutMs: RUN_REQUEST_TIMEOUT_MS,
    }),
  /** Standard-output text of one Segment of a set or set-preview run (Req 5.4, 5.10). */
  getSegmentText: (runId: string, index: number) =>
    request(s.segmentTextResponse, `/runs/${encodeURIComponent(runId)}/segments/${index}/text`),
  /** A new, started suggest run on the Segment's pages of the run's PDF ("Create a blueprint from these pages", Req 5.4). */
  createSampleFromPages: (runId: string, index: number) =>
    request(s.runSchema, `/runs/${encodeURIComponent(runId)}/segments/${index}/sample`, {
      method: 'POST',
      timeoutMs: RUN_REQUEST_TIMEOUT_MS,
    }),

  createPackage: (body: z.input<typeof s.createPackageRequest>) =>
    request(s.packageSchema, '/packages', { method: 'POST', body }),
  getPackage: (id: string) => request(s.packageDetailResponse, `/packages/${encodeURIComponent(id)}`),
  myPackages: () => request(s.packageListResponse, '/packages', { query: { mine: 'true' } }),
  sharedPackages: () => request(s.packageListResponse, '/packages/shared'),
  downloadPackage: (id: string) => request(s.downloadResponse, `/packages/${encodeURIComponent(id)}/download`),
  rebuildPackage: (id: string) => request(empty, `/packages/${encodeURIComponent(id)}/rebuild`, { method: 'POST' }),
  addShares: (id: string, recipients: s.Recipient[]) =>
    request(z.object({ items: z.array(s.shareSchema) }), `/packages/${encodeURIComponent(id)}/shares`, {
      method: 'POST',
      body: { recipients },
    }),
  revokeShare: (id: string, recipientKey: string) =>
    request(empty, `/packages/${encodeURIComponent(id)}/shares/${encodeURIComponent(recipientKey)}`, {
      method: 'DELETE',
    }),

  adminSummary: (from: string, to: string) =>
    request(s.adminSummaryResponse, '/admin/summary', { query: { from, to } }),
  adminUsers: (from: string, to: string) => request(s.adminUsersResponse, '/admin/users', { query: { from, to } }),
  adminTimeline: (sub: string, from: string, to: string, cursor?: string) =>
    request(s.adminTimelineResponse, `/admin/users/${encodeURIComponent(sub)}/timeline`, {
      query: { from, to, cursor },
    }),
  adminUserBlueprints: (sub: string) =>
    request(s.adminBlueprintListResponse, `/admin/users/${encodeURIComponent(sub)}/blueprints`),
  adminBlueprints: (q: {
    q?: string;
    owner?: string;
    docType?: string;
    archived?: 'true' | 'false';
    cursor?: string;
  }) => request(s.adminBlueprintListResponse, '/admin/blueprints', { query: q }),
  adminSchema: (id: string, v: number) =>
    requestBlob(`/admin/blueprints/${encodeURIComponent(id)}/versions/${v}/schema`),
  adminExport: (kind: 'users' | 'blueprints' | 'summary', from: string, to: string) =>
    requestBlob(`/admin/export/${kind}.csv`, { from, to }),
};

export type Api = typeof api;
